import dynamic from 'next/dynamic';
import type { OnCopyProps, ThemeObject } from 'react-json-view';

type PrettyJsonVisualizerProps = {
  json: string;
};

const ReactJson = dynamic(() => import('react-json-view'), {
  ssr: false,
});

const footprintTheme: ThemeObject = {
  base00: 'transparent', // background
  base01: '#f7f7f7', // lighter background
  base02: '#e2e2e2', // selection background
  base03: '#707070', // comments, line numbers
  base04: '#8d8d8d', // dark foreground
  base05: '#2d2d2d', // default foreground
  base06: '#444444', // light foreground
  base07: '#161616', // lightest foreground (keys)
  base08: '#bf140a', // null, undefined, functions
  base09: '#986801', // numbers, booleans
  base0A: '#c18401', // classes
  base0B: '#0c845c', // strings
  base0C: '#24585b', // support, regex
  base0D: '#24585b', // functions, methods (expand icons)
  base0E: '#a626a4', // keywords
  base0F: '#986801', // deprecated
};

const PrettyJsonVisualizer = ({ json }: PrettyJsonVisualizerProps) => {
  let parsedJson: object | null = null;

  try {
    parsedJson = JSON.parse(json);
  } catch {
    return <span className="text-primary text-body-2">Invalid JSON</span>;
  }

  if (!parsedJson) {
    return <span className="text-primary text-body-2">Invalid JSON</span>;
  }

  const handleCopy = (copiedValue: OnCopyProps) => {
    navigator.clipboard.writeText(JSON.stringify(copiedValue.src, null, 2));
    return false;
  };

  return (
    <ReactJson
      src={parsedJson}
      enableClipboard={handleCopy}
      iconStyle="triangle"
      theme={footprintTheme}
      style={{
        fontFamily: 'var(--font-family-code)',
        fontSize: '0.8125rem',
        lineHeight: '1.25rem',
      }}
      displayDataTypes={false}
      quotesOnKeys={false}
    />
  );
};

export default PrettyJsonVisualizer;
